import { create } from 'zustand';
import type { Collection, CollectionsResponse, CollectionUpdate } from '../types';
import { collectionService } from '../services/collections-api';
import { toast } from 'sonner';

interface CollectionStore {
  collections: Collection[];
  loading: boolean;
  error: Error | null;
  lastFetchTimestamp: number;
  isFetching: boolean;
  CACHE_DURATION: number;
  fetchCollections: () => Promise<void>;
  createCollection: (name: string, description?: string) => Promise<Collection>;
  updateCollection: (id: number, data: Partial<Collection>) => Promise<Collection>;
  deleteCollection: (id: number) => Promise<void>;
  getCollectionsFromCache: () => Collection[];
  getCollectionsByIds: (ids: number[]) => Collection[];
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache duration
const COLLECTIONS_CACHE_KEY = 'easylinks_collections_cache';

// Helper function to get collections from localStorage
const getCollectionsFromLocalStorage = (): Collection[] => {
  try {
    const cachedCollections = localStorage.getItem(COLLECTIONS_CACHE_KEY);
    return cachedCollections ? JSON.parse(cachedCollections) : [];
  } catch (error) {
    console.error('Error reading collections from localStorage:', error);
    return [];
  }
};

// Helper function to save collections to localStorage
const saveCollectionsToLocalStorage = (collections: Collection[]) => {
  try {
    localStorage.setItem(COLLECTIONS_CACHE_KEY, JSON.stringify(collections));
  } catch (error) {
    console.error('Error saving collections to localStorage:', error);
  }
};

const useCollectionStore = create<CollectionStore>((set, get) => ({
  collections: getCollectionsFromLocalStorage(),
  loading: false,
  error: null,
  lastFetchTimestamp: 0,
  isFetching: false,
  CACHE_DURATION,

  fetchCollections: async () => {
    const store = get();
    const now = Date.now();

    // Log store state for debugging
    console.log('Current store state:', {
      collectionsCount: store.collections.length,
      isFetching: store.isFetching,
      lastFetchTimestamp: store.lastFetchTimestamp,
      timeSinceLastFetch: now - store.lastFetchTimestamp
    });

    // Return cached data if within cache duration and we have collections
    if (
      store.collections.length > 0 && 
      !store.isFetching && 
      (now - store.lastFetchTimestamp) < store.CACHE_DURATION
    ) {
      console.log('Using cached collections');
      return;
    }

    // Prevent concurrent fetches
    if (store.isFetching) {
      console.log('Already fetching collections');
      return;
    }

    try {
      set({ loading: true, isFetching: true, error: null });
      const response = await collectionService.getCollections();
      const collections = response.items;

      // Save to localStorage and update state
      saveCollectionsToLocalStorage(collections);
      set({ 
        collections,
        loading: false,
        isFetching: false,
        lastFetchTimestamp: now,
        error: null 
      });
    } catch (error) {
      console.error('Error fetching collections:', error);
      set({ 
        loading: false,
        isFetching: false,
        error: error as Error 
      });
      toast.error('Failed to fetch collections');
    }
  },

  createCollection: async (name: string, description?: string) => {
    try {
      set({ loading: true, error: null });
      const newCollection = await collectionService.createCollection({ name, description });

      // Update localStorage and state
      const currentCollections = get().collections;
      const updatedCollections = [...currentCollections, newCollection];
      saveCollectionsToLocalStorage(updatedCollections);
      set({ collections: updatedCollections, loading: false });

      toast.success('Collection created successfully');
      return newCollection;
    } catch (error) {
      console.error('Error creating collection:', error);
      set({ loading: false, error: error as Error });
      toast.error('Failed to create collection');
      throw error;
    }
  },

  updateCollection: async (id: number, data: Partial<Collection>) => {
    try {
      set({ loading: true, error: null });
      const updatedCollection = await collectionService.updateCollection(id, data as CollectionUpdate);

      // Update localStorage and state
      const currentCollections = get().collections;
      const updatedCollections = currentCollections.map(collection => 
        collection.id === id ? updatedCollection : collection
      );
      saveCollectionsToLocalStorage(updatedCollections);
      set({ collections: updatedCollections, loading: false });

      toast.success('Collection updated successfully');
      return updatedCollection;
    } catch (error) {
      console.error('Error updating collection:', error);
      set({ loading: false, error: error as Error });
      toast.error('Failed to update collection');
      throw error;
    }
  },

  deleteCollection: async (id: number) => {
    try {
      set({ loading: true, error: null });
      await collectionService.deleteCollection(id);

      // Update localStorage and state
      const currentCollections = get().collections;
      const updatedCollections = currentCollections.filter(collection => collection.id !== id);
      saveCollectionsToLocalStorage(updatedCollections);
      set({ collections: updatedCollections, loading: false });

      toast.success('Collection deleted successfully');
    } catch (error) {
      console.error('Error deleting collection:', error);
      set({ loading: false, error: error as Error });
      toast.error('Failed to delete collection');
      throw error;
    }
  },

  getCollectionsFromCache: () => {
    return getCollectionsFromLocalStorage();
  },

  getCollectionsByIds: (ids) => {
    const allCollections = get().collections;
    return ids.map(id => allCollections.find(collection => collection.id === id)).filter(Boolean) as Collection[];
  }
}));

export default useCollectionStore; 